import { useState, useEffect, useRef, useCallback } from 'react'

// ─── ENTSOE API Configuration ───────────────────────────────────────
const ENTSOE_TOKEN = 'a9cdd3d3-6e95-4279-95de-b9bc918030db'
const ENTSOE_BASE = 'https://web-api.tp.entsoe.eu/api'
const CORS_PROXY = 'https://corsproxy.io/?'

// ─── Area EIC Codes ─────────────────────────────────────────────────
const AREA_CODES: Record<string, string> = {
  // Countries
  Germany: '10Y1001A1001A83F',
  'United Kingdom': '10Y1001A1001A92E',
  UK: '10Y1001A1001A92E',
  France: '10YFR-RTE------C',
  Spain: '10YES-REE------0',
  'Iberian Peninsula': '10YES-REE------0',
  Italy: '10YIT-GRTN-----B',
  Netherlands: '10YNL----------L',
  Poland: '10YPL-AREA-----S',
  Sweden: '10YSE-1--------K',
  Nordic: '10YSE-1--------K',
  Norway: '10YNO-0--------C',
  // Cities → country codes
  Berlin: '10Y1001A1001A83F',
  London: '10Y1001A1001A92E',
  Paris: '10YFR-RTE------C',
  Madrid: '10YES-REE------0',
  Rome: '10YIT-GRTN-----B',
  Amsterdam: '10YNL----------L',
  Warsaw: '10YPL-AREA-----S',
  Stockholm: '10YSE-1--------K',
  Oslo: '10YNO-0--------C',
  Munich: '10Y1001A1001A83F',
  Hamburg: '10Y1001A1001A83F',
  // Regions
  'Central Eastern Europe': '10Y1001A1001A83F',
  Balkans: '10YCS-SERBIATSOV',
  'North Sea': '10Y1001A1001A83F',
  'Baltic Sea': '10YSE-1--------K',
  Mediterranean: '10YIT-GRTN-----B',
  // Wind regions → country codes
  'UK Wind 100m': '10Y1001A1001A92E',
  'Europe Wind 100m': '10Y1001A1001A83F',
  'US Wind 100m': '', // No ENTSOE coverage
  'Nordic Wind 100m': '10YSE-1--------K',
}

// ─── Variable → ENTSOE Document Type mapping ────────────────────────
interface ENTSOEQuery {
  documentType: string
  processType?: string
  priceDomain?: boolean // use in_Domain for price queries
  valueField: 'price.amount' | 'quantity' // which XML field holds the value
}

const VARIABLE_MAP: Record<string, ENTSOEQuery> = {
  'Day-ahead Prices': {
    documentType: 'A44',
    priceDomain: true,
    valueField: 'price.amount',
  },
  'Imbalance Prices': {
    documentType: 'A85',
    processType: 'A16',
    valueField: 'price.amount',
  },
  'Generation (Actual)': {
    documentType: 'A75',
    processType: 'A16',
    valueField: 'quantity',
  },
  'Generation Forecast (Day-ahead)': {
    documentType: 'A71',
    processType: 'A01',
    valueField: 'quantity',
  },
  'Wind & Solar Forecast (Day-ahead)': {
    documentType: 'A69',
    processType: 'A01',
    valueField: 'quantity',
  },
  'Wind & Solar Forecast (Intraday)': {
    documentType: 'A69',
    processType: 'A40',
    valueField: 'quantity',
  },
  'Load (Actual)': {
    documentType: 'A65',
    processType: 'A16',
    valueField: 'quantity',
  },
  'Load Forecast (Day-ahead)': {
    documentType: 'A65',
    processType: 'A01',
    valueField: 'quantity',
  },
  'Imbalance Volumes': {
    documentType: 'A86',
    processType: 'A16',
    valueField: 'quantity',
  },
  'Scheduled Exchanges (Day-ahead)': {
    documentType: 'A09',
    processType: 'A01',
    valueField: 'quantity',
  },
  'Scheduled Exchanges (Total)': {
    documentType: 'A25',
    processType: 'A16',
    valueField: 'quantity',
  },
  'Cross-border Flows': {
    documentType: 'A11',
    processType: 'A16',
    valueField: 'quantity',
  },
  'Available Hydro Capacity': {
    documentType: 'A72',
    processType: 'A01',
    valueField: 'quantity',
  },
  'NTC (Day-ahead)': {
    documentType: 'A61',
    processType: 'A01',
    valueField: 'quantity',
  },
  'NTC Weekahead': {
    documentType: 'A61',
    processType: 'A02',
    valueField: 'quantity',
  },
  'NTC Monthahead': {
    documentType: 'A61',
    processType: 'A03',
    valueField: 'quantity',
  },
  'NTC Yearahead': {
    documentType: 'A61',
    processType: 'A04',
    valueField: 'quantity',
  },
  'Net Position (Day-ahead)': {
    documentType: 'A25',
    processType: 'A01',
    valueField: 'quantity',
  },
  'Net Position (Total)': {
    documentType: 'A25',
    processType: 'A16',
    valueField: 'quantity',
  },
}

// ─── Exported helpers ───────────────────────────────────────────────

/** Check if a variable name is an ENTSOE variable */
export function isENTSOEVariable(variable: string): boolean {
  return variable in VARIABLE_MAP
}

/** Get the ENTSOE area code for a region, or empty string if not covered */
export function getENTSOEAreaCode(region: string): string {
  return AREA_CODES[region] || ''
}

// ─── Data types ─────────────────────────────────────────────────────

export interface ENTSOEDataPoint {
  dt: number // Unix timestamp (seconds)
  value: number
  label: string // formatted time label
}

interface CacheEntry {
  data: ENTSOEDataPoint[]
  timestamp: number
}

const entsoeCache: Record<string, CacheEntry> = {}
const CACHE_TTL = 15 * 60 * 1000 // 15 minutes

// ─── XML Parsing ────────────────────────────────────────────────────

function parseENTSOEXml(
  xmlText: string,
  valueField: 'price.amount' | 'quantity',
): ENTSOEDataPoint[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlText, 'text/xml')

  // Check for error response
  const reason = doc.querySelector('Reason text')
  if (reason) {
    throw new Error(`ENTSOE API: ${reason.textContent}`)
  }

  const points: ENTSOEDataPoint[] = []

  // Find all TimeSeries
  const timeSeries = doc.querySelectorAll('TimeSeries')

  for (const ts of timeSeries) {
    const periods = ts.querySelectorAll('Period')

    for (const period of periods) {
      // Get period start time
      const startText =
        period.querySelector('timeInterval start')?.textContent ||
        period.querySelector('start')?.textContent
      if (!startText) continue

      const startTime = new Date(startText)

      // Get resolution (PT15M, PT30M, PT60M, etc.)
      const resolution =
        period.querySelector('resolution')?.textContent || 'PT60M'
      const resolutionMinutes = parseResolution(resolution)

      // Get all points in this period
      const xmlPoints = period.querySelectorAll('Point')

      for (const point of xmlPoints) {
        const position = parseInt(
          point.querySelector('position')?.textContent || '0',
        )
        if (position === 0) continue

        // Value field varies: price.amount for prices, quantity for volumes
        let value: number
        if (valueField === 'price.amount') {
          value = parseFloat(
            point.querySelector('price\\.amount')?.textContent ||
              point.querySelector('price_amount')?.textContent ||
              // Try getting any child that contains the price
              getNestedText(point, 'price.amount') ||
              '0',
          )
        } else {
          value = parseFloat(
            point.querySelector('quantity')?.textContent || '0',
          )
        }

        // Calculate timestamp: start + (position - 1) * resolution
        const dt = new Date(
          startTime.getTime() + (position - 1) * resolutionMinutes * 60 * 1000,
        )
        const dtUnix = Math.floor(dt.getTime() / 1000)

        // Format label
        const label = dt.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })

        points.push({ dt: dtUnix, value, label })
      }
    }
  }

  // Sort by timestamp and deduplicate
  points.sort((a, b) => a.dt - b.dt)
  const seen = new Set<number>()
  return points.filter((p) => {
    if (seen.has(p.dt)) return false
    seen.add(p.dt)
    return true
  })
}

/** Helper to get text from nested elements with dots in names */
function getNestedText(parent: Element, path: string): string {
  // Try direct child text nodes
  for (const child of parent.children) {
    const tag = child.tagName.toLowerCase()
    if (
      tag === path.toLowerCase() ||
      tag === path.replace('.', '_').toLowerCase()
    ) {
      return child.textContent || ''
    }
  }
  // For "price.amount", try finding <price><amount> nested structure
  const parts = path.split('.')
  if (parts.length === 2) {
    const outer = parent.querySelector(parts[0])
    if (outer) {
      const inner = outer.querySelector(parts[1])
      if (inner) return inner.textContent || ''
    }
  }
  return ''
}

function parseResolution(resolution: string): number {
  const match = resolution.match(/PT(\d+)([MH])/)
  if (!match) return 60
  const val = parseInt(match[1])
  const unit = match[2]
  return unit === 'H' ? val * 60 : val
}

// ─── Date formatting for ENTSOE API ────────────────────────────────

function formatENTSOEDate(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')
}

// ─── Build query URL ────────────────────────────────────────────────

function buildENTSOEUrl(
  variable: string,
  areaCode: string,
  periodStart: Date,
  periodEnd: Date,
): string {
  const query = VARIABLE_MAP[variable]
  if (!query) return ''

  const params = new URLSearchParams({
    securityToken: ENTSOE_TOKEN,
    documentType: query.documentType,
    periodStart: formatENTSOEDate(periodStart),
    periodEnd: formatENTSOEDate(periodEnd),
  })

  if (query.processType) {
    params.set('processType', query.processType)
  }

  if (query.priceDomain) {
    params.set('in_Domain', areaCode)
    params.set('out_Domain', areaCode)
  } else {
    params.set('in_Domain', areaCode)
  }

  return `${ENTSOE_BASE}?${params.toString()}`
}

// ─── Main Hook ──────────────────────────────────────────────────────

export function useENTSOEData(
  variable: string,
  region: string,
  periodDays: number = 2,
) {
  const [data, setData] = useState<ENTSOEDataPoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const isENTSOE = isENTSOEVariable(variable)
  const areaCode = getENTSOEAreaCode(region)

  useEffect(() => {
    // Skip if not an ENTSOE variable or no area code
    if (!isENTSOE || !areaCode) {
      setData([])
      setLoading(false)
      setError(
        isENTSOE && !areaCode ? `No ENTSOE coverage for ${region}` : null,
      )
      return
    }

    // Check cache
    const cacheKey = `entsoe_${variable}_${areaCode}_${periodDays}`
    const cached = entsoeCache[cacheKey]
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setData(cached.data)
      setLoading(false)
      setError(null)
      return
    }

    // Abort previous request
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setError(null)

    const now = new Date()
    const periodEnd = new Date(now)
    periodEnd.setUTCHours(periodEnd.getUTCHours() + 1, 0, 0, 0) // round up to next hour

    const periodStart = new Date(periodEnd)
    periodStart.setUTCDate(periodStart.getUTCDate() - periodDays)

    const directUrl = buildENTSOEUrl(variable, areaCode, periodStart, periodEnd)
    if (!directUrl) {
      setError('Unknown ENTSOE variable')
      setLoading(false)
      return
    }

    const proxyUrl = `${CORS_PROXY}${encodeURIComponent(directUrl)}`

    const query = VARIABLE_MAP[variable]

    fetch(proxyUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `ENTSOE API returned ${res.status}: ${res.statusText}`,
          )
        }
        return res.text()
      })
      .then((xmlText) => {
        const parsed = parseENTSOEXml(xmlText, query.valueField)
        if (parsed.length === 0) {
          throw new Error('No data points returned from ENTSOE')
        }

        // Cache the result
        entsoeCache[cacheKey] = { data: parsed, timestamp: Date.now() }

        setData(parsed)
        setLoading(false)
        setError(null)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        console.warn('ENTSOE fetch failed, will use mock data:', err.message)
        setError(err.message)
        setData([])
        setLoading(false)
      })

    return () => controller.abort()
  }, [variable, region, periodDays, isENTSOE, areaCode])

  return { data, loading, error, isENTSOE, hasRealData: data.length > 0 }
}
